import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { SecureImage } from "@/components/SecureImage";
import { EmptyState, Loader } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useCan } from "@/lib/perms";
import { StockQtyEditor } from "@/components/DemandSummary";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UNITS } from "@/lib/constants";
import { Package, Plus, Upload, ArrowLeft, Trash2, Pencil, FolderPlus, ChevronsUpDown, Check } from "lucide-react";

export default function Products() {
  const isVendor = true;
  const can = useCan();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [products, setProducts] = useState([]);
  const [catModal, setCatModal] = useState(false);
  const [prodModal, setProdModal] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    const r = await api.get("/categories");
    setCats(r.data.categories);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCat = async (c) => {
    setActive(c);
    const r = await api.get(`/products?category_id=${c.id}`);
    setProducts(r.data.products);
  };

  if (loading) return <Loader />;

  if (active) {
    const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
    return (
      <div className="space-y-6" data-testid="products-category-view">
        <button onClick={() => { setActive(null); load(); }} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-[#0D5C3A]" data-testid="products-back-btn"><ArrowLeft className="h-4 w-4" /> All categories</button>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <SecureImage fileId={active.image_file_id} className="h-14 w-14 rounded-xl object-cover" />
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-stone-900">{active.name}</h1>
              <p className="text-sm text-stone-500">{products.length} product(s)</p>
            </div>
          </div>
          {can("catalog.create") && <Button data-testid="add-product-btn" onClick={() => setProdModal(true)} className="bg-[#0D5C3A] hover:bg-[#084229]"><Plus className="mr-1 h-4 w-4" /> Add Product</Button>}
        </div>
        <Input data-testid="product-search-input" placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm bg-white" />
        {filtered.length === 0 ? (
          <EmptyState icon={Package} title="No products yet" description="Add your first product to this category." testid="products-empty" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((p) => <ProductCard key={p.id} p={p} cat={active} cats={cats} onChange={() => openCat(active)} />)}
          </div>
        )}
        <ProductModal open={prodModal} onOpenChange={setProdModal} category={active} onSaved={() => openCat(active)} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">Products</h1>
          <p className="mt-1 text-stone-500">Organize your supplies into categories.</p>
        </div>
        {can("catalog.create") && <Button data-testid="add-category-btn" onClick={() => setCatModal(true)} className="bg-[#0D5C3A] hover:bg-[#084229]"><Plus className="mr-1 h-4 w-4" /> Add Category</Button>}
      </div>

      {cats.length === 0 ? (
        <EmptyState icon={FolderPlus} title="Create your first category to start adding products." description="Products must belong to a category." testid="categories-empty"
          action={<Button data-testid="empty-add-category-btn" onClick={() => setCatModal(true)} className="bg-[#0D5C3A] hover:bg-[#084229]"><Plus className="mr-1 h-4 w-4" /> Add Category</Button>} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {cats.map((c) => <CategoryCard key={c.id} c={c} onOpen={openCat} onChange={load} />)}
        </div>
      )}
      <CategoryModal open={catModal} onOpenChange={setCatModal} onSaved={load} />
    </div>
  );
}

function CategoryCard({ c, onOpen, onChange }) {
  const can = useCan();
  const [del, setDel] = useState(false);
  const [edit, setEdit] = useState(false);
  const remove = async () => {
    try { await api.delete(`/categories/${c.id}`); toast.success("Category deleted"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
    setDel(false);
  };
  return (
    <div className="group overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white shadow-sm transition-all hover:shadow-md" data-testid={`category-card-${c.id}`}>
      <button onClick={() => onOpen(c)} className="block w-full">
        <SecureImage fileId={c.image_file_id} className="h-32 w-full object-cover" />
      </button>
      <div className="flex items-center justify-between p-4">
        <button onClick={() => onOpen(c)} className="text-left">
          <p className="font-heading font-semibold text-stone-900">{c.name}</p>
          <p className="text-xs text-stone-500">{c.product_count} product(s)</p>
        </button>
        <div className="flex gap-1">
          {can("catalog.create") && <button onClick={() => onOpen(c)} className="rounded-lg p-2 text-[#0D5C3A] hover:bg-[#F0ECE1]" title="Add product" data-testid={`category-open-${c.id}`}><Plus className="h-4 w-4" /></button>}
          {can("catalog.edit") && <button onClick={() => setEdit(true)} className="rounded-lg p-2 text-stone-500 hover:bg-[#F0ECE1]" title="Rename category" data-testid={`category-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>}
          {can("catalog.delete") && <button onClick={() => setDel(true)} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50" data-testid={`category-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>}
        </div>
      </div>
      {edit && <CategoryModal open={edit} onOpenChange={setEdit} category={c} onSaved={onChange} />}
      <AlertDialog open={del} onOpenChange={setDel}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {c.name}?</AlertDialogTitle>
            <AlertDialogDescription>Categories with products cannot be deleted until products are removed. Order history is preserved.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-category">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProductCard({ p, cat, cats, onChange }) {
  const { business } = useAuth();
  const can = useCan();
  const isVendor = true;
  const inStock = p.in_stock !== false;
  const [del, setDel] = useState(false);
  const [edit, setEdit] = useState(false);
  const remove = async () => { await api.delete(`/products/${p.id}`); toast.success("Product removed"); onChange(); setDel(false); };
  const toggleStock = async () => {
    try { await api.post(`/products/${p.id}/stock`, { in_stock: !inStock }); toast.success(inStock ? "Marked out of stock" : "Marked in stock"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white shadow-sm" data-testid={`product-card-${p.id}`}>
      <SecureImage fileId={p.image_file_id} className="h-28 w-full object-cover" />
      <div className="p-4">
        <p className="font-heading font-semibold text-stone-900">{p.name}</p>
        <p className="text-xs text-stone-500">{p.unit}</p>
        {p.price != null && <p className="mt-0.5 text-sm font-semibold text-[#0D5C3A]" data-testid={`product-price-${p.id}`}>₹{Number(p.price).toFixed(2)} <span className="text-xs font-normal text-stone-400">/ {p.unit}</span></p>}
        <p className="mt-0.5 text-xs text-stone-500">Stock on hand: <StockQtyEditor productId={p.id} value={p.stock_qty} unit={p.unit} onSaved={onChange} testid={`product-stock-qty-${p.id}`} /></p>
        {isVendor && !inStock && <span className="mt-1 inline-block rounded bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-600" data-testid={`product-oos-badge-${p.id}`}>Out of Stock</span>}
        <div className="mt-3 flex items-center gap-1">
          {can("catalog.edit") && <button onClick={() => setEdit(true)} className="rounded-lg p-2 text-stone-600 hover:bg-stone-100" data-testid={`product-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>}
          {can("catalog.delete") && <button onClick={() => setDel(true)} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50" data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>}
          {isVendor && can("catalog.edit") && (
            <button onClick={toggleStock} data-testid={`product-stock-toggle-${p.id}`}
              className={`ml-auto rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${inStock ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-rose-50 text-rose-600 hover:bg-rose-100"}`}>
              {inStock ? "In stock" : "Out of stock"}
            </button>
          )}
        </div>
      </div>
      <ProductModal open={edit} onOpenChange={setEdit} category={cat} cats={cats} product={p} onSaved={onChange} />
      <AlertDialog open={del} onOpenChange={setDel}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove {p.name}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone. Historical orders are preserved.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-product">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CategoryModal({ open, onOpenChange, onSaved, category }) {
  const [name, setName] = useState(category?.name || "");
  const [fileId, setFileId] = useState(category?.image_file_id || null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!name.trim()) { toast.error("Category name is required"); return; }
    setBusy(true);
    try {
      if (category) { await api.put(`/categories/${category.id}`, { name: name.trim(), image_file_id: fileId }); toast.success("Category updated"); }
      else { await api.post("/categories", { name: name.trim(), image_file_id: fileId }); toast.success("Category added"); setName(""); setFileId(null); setFileName(""); }
      onSaved(); onOpenChange(false);
    }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="category-modal">
        <DialogHeader><DialogTitle>{category ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Category Name</Label>
            <Input data-testid="category-name-input" className="mt-1" value={name} maxLength={60} autoComplete="off" placeholder="Type any category, e.g. Steel, Chemicals, Fruits" onChange={(e) => setName(e.target.value)} />
          </div>
          <div><Label>Category Image (optional)</Label>
            <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#E5E0D8] px-4 py-3 text-sm text-stone-500 hover:border-[#0D5C3A]">
              <Upload className="h-4 w-4" /> {fileName || (category?.image_file_id ? "Replace image" : "Upload image")}
              <input data-testid="category-image-input" type="file" accept="image/*" className="hidden" onChange={async (e) => { if (e.target.files[0]) { const r = await uploadFile(e.target.files[0], "category"); setFileId(r.file_id); setFileName(r.filename); } }} />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="category-submit-btn" disabled={busy} onClick={save} className="bg-[#0D5C3A] hover:bg-[#084229]">{category ? "Save" : "Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductModal({ open, onOpenChange, category, cats, product, onSaved }) {
  const { business } = useAuth();
  const isVendor = true;
  const [name, setName] = useState(product?.name || "");
  const [unit, setUnit] = useState(product?.unit || "");
  const [price, setPrice] = useState(product?.price ?? "");
  const [fileId, setFileId] = useState(product?.image_file_id || null);
  const [fileName, setFileName] = useState("");
  const [unitOpen, setUnitOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [catId, setCatId] = useState(category?.id);
  useEffect(() => { if (open) { setName(product?.name || ""); setUnit(product?.unit || ""); setPrice(product?.price ?? ""); setFileId(product?.image_file_id || null); setFileName(""); setCatId(category?.id); } }, [open, product, category]);
  const save = async () => {
    if (!name.trim() || !unit) { toast.error("Name and unit are required"); return; }
    setBusy(true);
    const body = { name, unit, category_id: product ? catId : category.id, image_file_id: fileId, price: price === "" ? null : parseFloat(price) };
    const moved = product && catId !== category.id;
    try {
      if (product) await api.put(`/products/${product.id}`, body); else await api.post("/products", body);
      toast.success(moved ? `Moved to ${cats.find((c) => c.id === catId)?.name}` : product ? "Product updated" : "Product saved"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-modal">
        <DialogHeader><DialogTitle>{product ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Product Name *</Label><Input data-testid="product-name-input" className="mt-1" value={name} onChange={(e) => setName(e.target.value)} /></div>
          <div><Label>Category</Label>
            {product && cats?.length > 1 ? (
              <Select value={catId} onValueChange={setCatId}>
                <SelectTrigger className="mt-1" data-testid="product-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`product-category-option-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : <Input className="mt-1 bg-stone-50" value={category?.name} disabled />}
          </div>
          <div><Label>Unit of Measurement *</Label>
            <Popover open={unitOpen} onOpenChange={setUnitOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" data-testid="product-unit-trigger" className="mt-1 w-full justify-between font-normal">
                  {unit || "Select unit…"} <ChevronsUpDown className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command>
                  <CommandInput placeholder="Search units…" data-testid="product-unit-search" />
                  <CommandList><CommandEmpty>No unit found.</CommandEmpty>
                    <CommandGroup>{UNITS.map((u) => (
                      <CommandItem key={u} value={u} onSelect={() => { setUnit(u); setUnitOpen(false); }} data-testid={`unit-option-${u}`}>
                        <Check className={`mr-2 h-4 w-4 ${unit === u ? "opacity-100" : "opacity-0"}`} /> {u}
                      </CommandItem>
                    ))}</CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          {isVendor && (
            <div><Label>Price per unit (₹) <span className="text-xs text-stone-400">— used to auto-calculate order totals</span></Label>
              <Input data-testid="product-price-input" type="number" min="0" step="0.01" className="mt-1" placeholder="e.g. 120" value={price} onChange={(e) => setPrice(e.target.value)} /></div>
          )}
          <div><Label>Product Image (optional)</Label>
            <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-[#E5E0D8] px-4 py-3 text-sm text-stone-500 hover:border-[#0D5C3A]">
              <Upload className="h-4 w-4" /> {fileName || "Upload image"}
              <input data-testid="product-image-input" type="file" accept="image/*" className="hidden" onChange={async (e) => { if (e.target.files[0]) { const r = await uploadFile(e.target.files[0], "product"); setFileId(r.file_id); setFileName(r.filename); } }} />
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="product-submit-btn" disabled={busy} onClick={save} className="bg-[#0D5C3A] hover:bg-[#084229]">Save Product</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
